import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles, useTheme } from '@material-ui/core/styles';
import cloneDeep from 'lodash/cloneDeep';
import { FormGroup, FormControlLabel, Checkbox, Button, Grid, FormLabel } from '@material-ui/core';
import debounce from 'lodash/debounce';
import ZoneFilters from './ZoneFilters';
import {
  verifyZoneTypeData,
  verifyZoneTypeRange,
  verifyZoneTypeDataOnChange,
  formattedValue,
} from '../../../helpers/pricing';

const styles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    padding: '10px',
    flexGrow: 1,
    position: 'relative',
  },
  fullWidth: {
    width: '100%',
    marginRight: '5px',
  },
  typoAlignment: {
    top: '25px',
    position: 'relative',
  },
  setButton: {
    marginTop: '10px',
    width: '124px',
    height: '32px',
  },
  setButtonDark: {
    marginTop: '10px',
    width: '124px',
    height: '32px',
    backgroundColor: '#9C27B0',
  },
  cancelButton: {
    width: '82px',
    height: '32px',
    marginTop: '10px',
  },
  cancelButtonDark: {
    marginTop: '10px',
    width: '82px',
    height: '32px',
    border: '1px solid #A8A8A8',
    backgroundColor: 'transparent',
    '& .MuiButton-label': {
      color: '#A8A8A8',
    },
    '&:hover': {
      background: 'transparent',
    },
  },
  inputTextMarginRight: {
    marginRight: '5px',
  },
  inputLabelZone: {
    position: 'absolute',
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  labelControl: {
    pointerEvents: 'none',
    '& .MuiFormLabel-root': {
      color: '#000000',
    },
  },
  labelControlDark: {
    pointerEvents: 'none',
    '& .MuiFormLabel-root': {
      color: '#BEBEBE',
    },
  },
  checkbox: {
    pointerEvents: 'auto',
    color: '#9C27B0 !important',
  },
  advanceComparison: {
    maxWidth: '100%',
  },
});

function GroupAdvanceComparison({
  classes,
  comparisonsZones,
  handleMarketModalClose,
  isOV,
  handleOV,
  saveZoneFilters,
  pricingGroup,
}) {
  const theme = useTheme();
  const [formValues, setFormValues] = useState([]);
  const [errorsFlags, setErrorsFlags] = useState([]);
  const [obstructedView, setObstructedView] = useState(isOV);

  const setComparisonCriteria = () => {
    handleOV(obstructedView);
    handleMarketModalClose();
  };

  const onChange = (_, checked) => {
    setObstructedView(checked);
  };

  const onSaveZoneFilters = (values, pg) => {
    const newErrors = cloneDeep(errorsFlags);
    values.forEach(z => {
      if (z.zoneType !== 'type') {
        if (!verifyZoneTypeData(z.type, z.low)) newErrors.push(`${z.zoneName}-low`);
        if (!verifyZoneTypeData(z.type, z.high)) newErrors.push(`${z.zoneName}-high`);
        if (!verifyZoneTypeRange(z.type, z.low, z.high)) newErrors.push(`${z.zoneName}-low`);
      }
    });
    setErrorsFlags(newErrors);

    if (newErrors.length) return;

    if (values && pg) saveZoneFilters(values, pg);
  };

  const debo = React.useRef(
    debounce((values, pg) => {
      onSaveZoneFilters(values, pg);
    }, 500)
  );

  const onChangeCallback = event => {
    const { name, value } = event.target;
    const zoneType = name.split('-')[name.split('-').length - 1];
    const zoneName = name.slice(0, name.length - (zoneType.length + 1));
    const zoneIndex = formValues.findIndex(zone => zone.zoneName === zoneName);
    const zoneToEdit = formValues[zoneIndex];

    if (zoneType !== 'type' && !verifyZoneTypeDataOnChange(zoneToEdit.type, value)) return;
    zoneToEdit[zoneType] = formattedValue(zoneType, zoneToEdit.type, value);
    formValues[zoneIndex] = zoneToEdit;
    const newErrors = cloneDeep(errorsFlags);
    newErrors.splice(newErrors.indexOf(name), 1);
    setErrorsFlags(newErrors);
    setFormValues(cloneDeep(formValues));

    debo.current(cloneDeep(formValues), pricingGroup);
  };

  useEffect(() => {
    const comparisonsZonesFiltered = comparisonsZones.reduce((acc, curZone) => {
      if (!acc.find(zone => zone.zone_name === curZone.zone_name)) {
        acc.push(curZone);
      }
      return acc;
    }, []);

    setFormValues(
      comparisonsZonesFiltered.map(zone => ({
        zoneName: zone.zone_name,
        sectionName: zone.section_name,
        type: zone.zone_type,
        low: zone.low_row,
        high: zone.high_row,
      }))
    );
  }, [comparisonsZones]);

  return (
    <FormGroup row className={classes.advanceComparison}>
      <Grid container>
        <Grid item xs={12}>
          <ZoneFilters
            formValues={formValues}
            onChangeCallback={onChangeCallback}
            classes={classes}
            // onBlurValidation={onBlurValidation}
            errorsFlags={errorsFlags}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <FormControlLabel
            className={theme.palette.type === 'light' ? classes.labelControl : classes.labelControlDark}
            control={<Checkbox checked={obstructedView} className={classes.checkbox} onChange={onChange} />}
            label={<FormLabel focused={false}>Exclude Obstructed View</FormLabel>}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.dialogActions} justifyContent="flex-end">
        <Grid item xs={2}>
          <Button
            variant="contained"
            onClick={handleMarketModalClose}
            fullWidth
            className={theme.palette.type === 'light' ? classes.cancelButton : classes.cancelButtonDark}
          >
            CANCEL
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            disabled={errorsFlags && errorsFlags.length > 0}
            variant="contained"
            color="primary"
            onClick={() => setComparisonCriteria()}
            fullWidth
            className={theme.palette.type === 'light' ? classes.setButton : classes.setButtonDark}
          >
            SET CRITERIA
          </Button>
        </Grid>
      </Grid>
    </FormGroup>
  );
}

GroupAdvanceComparison.propTypes = {
  classes: PropTypes.object.isRequired,
  comparisonsZones: PropTypes.array.isRequired,
  isOV: PropTypes.bool.isRequired,
  handleOV: PropTypes.func.isRequired,
  handleMarketModalClose: PropTypes.func.isRequired,
  saveZoneFilters: PropTypes.func.isRequired,
  pricingGroup: PropTypes.object.isRequired,
};

export default withStyles(styles)(GroupAdvanceComparison);
